import { useEffect, useRef, type ReactNode } from 'react'
import { XMarkIcon } from '@heroicons/react/20/solid'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  /** Secondary line under the title, e.g. a created-at date. */
  subtitle?: string
  children: ReactNode
  /** Action buttons rendered in the footer (right-aligned). */
  footer?: ReactNode
  /** Extra classes for the body wrapper (the element `aria-describedby` points to). */
  bodyClassName?: string
  describedById?: string
}

/**
 * Accessible modal shell: backdrop, focus trap, Escape-to-close, body scroll
 * lock, and a title/close header. Callers supply the body and footer. Shared by
 * NoteModal and TranscriptModal so the dialog a11y lives in one place.
 */
export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  bodyClassName = '',
  describedById = 'modal-description',
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  // Keep the latest onClose in a ref so the open effect can stay keyed on
  // `isOpen` alone. Otherwise a caller passing a fresh onClose each render
  // (e.g. an inline handler) would re-run the effect on every keystroke and
  // steal focus from the body back to the close button.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return

    // Focus the close button when the modal opens.
    closeButtonRef.current?.focus()

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
      }
    }

    const handleTab = (e: KeyboardEvent) => {
      if (!modalRef.current || e.key !== 'Tab') return

      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )

      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const isFocusInModal = modalRef.current.contains(document.activeElement)

      if (!isFocusInModal) {
        e.preventDefault()
        firstElement?.focus()
        return
      }

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    document.addEventListener('keydown', handleTab)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('keydown', handleTab)
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby={describedById}
    >
      <div
        ref={modalRef}
        className="bg-navy-light rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-navy-light shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h2 id="modal-title" className="text-xl font-semibold text-white mb-1">
              {title}
            </h2>
            {subtitle && <p className="text-gray-400 text-sm">{subtitle}</p>}
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="ml-4 p-2 rounded-lg bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 transition-colors focus:outline-none focus:ring-2 focus:ring-neon-cyan"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div id={describedById} className={bodyClassName}>
          {children}
        </div>

        {/* Footer */}
        {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  )
}
